export const documentsArticles = [
    {
        id: 'documents.overview',
        title: 'Documents Overview',
        category: 'Documents',
        order: 1,
        summary: 'Managing project documents and files',
        content: `
The Documents page provides a central repository for all your project files and documentation.

**Supported File Types:**

- PDF documents
- Images (JPG, PNG, etc.)
- Office documents (Word, Excel)
- CAD drawings (DWG, DXF)
- BIM models (IFC)

**Key Features:**

- Upload and organize files
- Preview documents in-browser
- Track version history
- Add comments and markup
- Define custom attributes
- Manage document workflows
    `,
        expandableSections: [
            {
                title: 'Document Grid',
                content: `
The grid shows all documents with:

- **Name** - File name
- **Type** - File type/extension
- **Size** - File size
- **Job** - Associated job
- **Uploaded** - Upload date
- **By** - Who uploaded it

Use folders to organize documents, or filter by job/type.
        `,
            },
            {
                title: 'Folder Structure',
                content: `
Organize documents into folders:

- Create folders for each job
- Use subfolders for categories (Drawings, Specs, Correspondence)
- Drag files between folders
- Share folder structures across jobs
        `,
            },
            {
                title: 'A document can live in several folders',
                content: `
Filing is by **link**, not by location — the same file can sit in more than one
folder at once. A supplier invoice can be in its job folder *and* in
**Company Admin / Accounts - Invoices & Bills**, and it is the same document in
both: rename it or upload a new version once and every folder shows the change.

- **Add to Folder…** (right-click) files documents somewhere new. Search the
  folder list by path — "admin invoice" finds *Company Admin / Accounts -
  Invoices & Bills* — and tick several folders to file into all of them at once.
  Documents never leave the folders they are already in.
- **Remove from "<folder>"** (right-click, shown when you have a folder open)
  takes the document out of *that* folder only. It is **not** a delete — the
  file stays in its other folders and in All Documents.
- **Delete** sends the document to the Recycle Bin, so it leaves *every* folder.
  Restoring it from the Recycle Bin puts it back in all of them.

The preview window lists every folder a document is filed in, under
**Details → Linked Entities → Folders**.
        `,
            },
        ],
        keywords: ['documents', 'files', 'attachments', 'drawings', 'PDFs'],
        route: '/documents',
        relatedArticles: ['documents.upload', 'documents.preview', 'documents.versions'],
    },
    {
        id: 'documents.upload',
        title: 'Uploading Documents',
        category: 'Documents',
        order: 2,
        summary: 'How to upload files to the document library',
        content: `
Upload documents to store them centrally and make them available to your team.

**Upload Methods:**

- Drag and drop files
- Click to browse and select
- Bulk upload multiple files
    `,
        expandableSections: [
            {
                title: 'Uploading Files',
                content: `
**Drag and Drop:**
1. Drag files from your computer
2. Drop onto the document area
3. Files upload automatically

**Browse and Select:**
1. Click **Upload** button
2. Select files in the dialog
3. Click **Open**

**Bulk Upload:**
- Select multiple files at once
- Or drag a folder (where supported)
- All files upload in parallel
        `,
            },
            {
                title: 'Upload Settings',
                content: `
When uploading, you can set:

- **Folder** - Where to store the file
- **Job** - Associated job
- **Attributes** - Custom metadata
- **Description** - Notes about the file

These can be set before or after upload.
        `,
            },
            {
                title: 'File Size Limits',
                content: `
Maximum file sizes:

- Individual files: 100 MB
- Batch upload: 500 MB total

For larger files, contact your administrator.
        `,
            },
        ],
        keywords: ['upload', 'add files', 'attach', 'import files'],
        route: '/documents',
        relatedArticles: ['documents.overview', 'documents.preview'],
    },
    {
        id: 'documents.preview',
        title: 'Document Preview',
        category: 'Documents',
        order: 3,
        summary: 'Previewing documents in the browser',
        content: `
Preview documents directly in the browser without downloading.

**Supported Previews:**

- PDF - Full preview with zoom and navigation
- Images - Image viewer with zoom
- Office - View Word/Excel/PowerPoint
- CAD - 2D drawing viewer
- BIM - 3D model viewer
    `,
        expandableSections: [
            {
                title: 'Using the Preview',
                content: `
1. Double-click a document
2. Or right-click > Preview
3. The preview opens in a panel

**Preview Controls:**
- Zoom in/out
- Navigate pages
- Download original
- Print
- Full screen mode
        `,
            },
            {
                title: 'CAD and BIM Viewing',
                content: `
**CAD Files (DWG/DXF):**
- 2D view with pan/zoom
- Layer visibility control
- Measurement tools

**BIM Files (IFC):**
- 3D model rotation
- Section views
- Element selection
- Property inspection
        `,
            },
        ],
        keywords: ['preview', 'view', 'viewer', 'open', 'display'],
        route: '/documents',
        relatedArticles: ['documents.overview', 'documents.upload'],
    },
    {
        id: 'documents.versions',
        title: 'Version History',
        category: 'Documents',
        order: 4,
        summary: 'Managing document versions and revisions',
        content: `
Track changes to documents over time with automatic version history.

**Version Features:**

- Automatic versioning on upload
- View previous versions
- Compare versions
- Restore old versions
    `,
        expandableSections: [
            {
                title: 'Viewing Version History',
                content: `
1. Select a document
2. Click **Version History** or right-click > Versions
3. See all versions with:
   - Version number
   - Upload date
   - Uploaded by
   - Size

Click a version to preview or download.
        `,
            },
            {
                title: 'Uploading New Versions',
                content: `
**Replace Document:**
1. Right-click the document
2. Select **Upload New Version**
3. Select the new file
4. The old version is preserved in history

**Drag and Drop:**
- Drop a file with the same name
- System asks if you want to replace
- Choose "New Version" to preserve history
        `,
            },
        ],
        keywords: ['versions', 'history', 'revisions', 'previous versions'],
        route: '/documents',
        relatedArticles: ['documents.overview', 'documents.upload'],
    },
    {
        id: 'documents.comments',
        title: 'Comments and Markup',
        category: 'Documents',
        order: 5,
        summary: 'Adding comments and annotations to documents',
        content: `
Add comments and markup to documents for collaboration and review.

**Comment Features:**

- Text comments on documents
- Pin comments to specific locations
- Reply to comments
- Resolve comment threads
    `,
        expandableSections: [
            {
                title: 'Adding Comments',
                content: `
1. Open document in preview
2. Click the **Comments** button
3. Click on the document to place a pin
4. Type your comment
5. Click **Post**

Comments appear in the sidebar and as pins on the document.
        `,
            },
            {
                title: 'Managing Comments',
                content: `
**Reply:**
Click a comment and type a reply to continue the thread.

**Resolve:**
Mark a comment as resolved when the issue is addressed.

**Filter:**
View only unresolved comments, or all comments.
        `,
            },
        ],
        keywords: ['comments', 'annotations', 'markup', 'review', 'feedback'],
        route: '/documents',
        relatedArticles: ['documents.overview', 'documents.preview'],
    },
    {
        id: 'documents.workflows',
        title: 'Document Workflows',
        category: 'Documents',
        order: 6,
        summary: 'Managing document review and approval workflows',
        content: `
Workflows allow you to route documents through review and approval processes.

**Workflow Types:**

- Review workflows
- Approval workflows
- Custom workflows
    `,
        expandableSections: [
            {
                title: 'Starting a Workflow',
                content: `
1. Select document(s)
2. Click **Start Workflow**
3. Choose workflow template
4. Select participants
5. Set due dates
6. Add instructions
7. Click **Start**

Participants receive notifications of their tasks.
        `,
            },
            {
                title: 'Participating in Workflows',
                content: `
When assigned to a workflow:

1. Notification appears in your tasks
2. Open the document
3. Review/approve as required
4. Add comments if needed
5. Mark as complete

The workflow advances to the next step.
        `,
            },
        ],
        keywords: ['workflow', 'review', 'approval', 'routing'],
        route: '/documents',
        relatedArticles: ['documents.overview', 'documents.comments'],
    },
    {
        id: 'documents.attributes',
        title: 'Custom Attributes',
        category: 'Documents',
        order: 7,
        summary: 'Defining and using custom document attributes',
        content: `
Custom attributes allow you to add structured metadata to documents.

**Attribute Types:**

- Text
- Number
- Date
- Selection (dropdown)
- Yes/No
    `,
        expandableSections: [
            {
                title: 'Defining Attributes',
                content: `
1. Click **Attribute Definitions**
2. Click **Add Attribute**
3. Configure:
   - Name
   - Type
   - Options (for selection)
   - Required (yes/no)
4. Save

The attribute becomes available for all documents.
        `,
            },
            {
                title: 'Using Attributes',
                content: `
**Setting Values:**
1. Select a document
2. Open properties panel
3. Set attribute values
4. Save

**Filtering by Attributes:**
Use the filter row to filter documents by attribute values.
        `,
            },
        ],
        keywords: ['attributes', 'metadata', 'properties', 'custom fields'],
        route: '/documents',
        relatedArticles: ['documents.overview'],
    },
    {
        id: 'documents.splitter',
        title: 'Drawing Splitter',
        category: 'Documents',
        order: 8,
        summary: 'Splitting multi-page PDFs into individual drawings',
        content: `
The Drawing Splitter helps you split a multi-page PDF into individual drawing files.

**Use Cases:**

- Split combined drawing sets
- Extract specific pages
- Rename pages with proper drawing numbers
    `,
        expandableSections: [
            {
                title: 'Using the Splitter',
                content: `
1. Select a multi-page PDF
2. Click **Split Drawing**
3. Preview all pages
4. For each page:
   - Set drawing number
   - Set title
   - Choose to include/exclude
5. Click **Split**

Individual PDFs are created for each selected page.
        `,
            },
        ],
        keywords: ['split', 'extract', 'drawing splitter', 'PDF split'],
        route: '/documents',
        relatedArticles: ['documents.overview', 'documents.upload'],
    },
];
